import {
  parse,
  print,
  visit,
  DocumentNode,
  OperationDefinitionNode,
  VariableDefinitionNode,
  ArgumentNode,
  ValueNode,
  ObjectValueNode,
  ObjectFieldNode,
} from 'graphql'

export function removeUnusedVariables(queryStr: string, variables: Record<string, any> = {}): string {
  const ast = parse(queryStr)

  // Get the set of provided variable names
  const providedVariables = new Set(Object.keys(variables))

  // Track which variables are actually used in the query
  const usedVariables = new Set<string>()

  // First pass: collect all variable references in the query
  visit(ast, {
    Variable(node) {
      usedVariables.add(node.name.value)
    },
  })

  // Helper function to filter value nodes (handles nested objects)
  const filterValue = (value: ValueNode): ValueNode | null => {
    if (value.kind === 'Variable') {
      const varName = value.name.value
      const varDef = findVariableDefinition(ast, varName)

      if (!varDef) return value // Keep if we can't find the definition

      const isRequired = varDef.type.kind === 'NonNullType'
      const isProvided = providedVariables.has(varName)
      const isUsed = usedVariables.has(varName)

      // Keep if: required OR (provided AND used in the query)
      return isRequired || (isProvided && isUsed) ? value : null
    } else if (value.kind === 'ObjectValue') {
      // Filter fields within the object
      const filteredFields = value.fields.reduce<ObjectFieldNode[]>((acc, field) => {
        const filteredValue = filterValue(field.value)
        if (filteredValue !== null) {
          acc.push({
            ...field,
            value: filteredValue,
          })
        }
        return acc
      }, [])

      // Return the object with filtered fields, or null if empty
      return filteredFields.length > 0 ? { ...value, fields: filteredFields } : null
    } else if (value.kind === 'ListValue') {
      // Handle list values recursively
      const filteredValues = value.values.map(v => filterValue(v)).filter((v): v is ValueNode => v !== null)

      return filteredValues.length > 0 ? { ...value, values: filteredValues } : null
    }

    // For all other value types (scalars, enums, etc.), keep as is
    return value
  }

  // Transform the AST
  const transformedAst = visit(ast, {
    OperationDefinition(node: OperationDefinitionNode) {
      if (!node.variableDefinitions || node.variableDefinitions.length === 0) {
        return node
      }

      // Filter variable definitions to keep only:
      // 1. Required variables (those with NonNullType)
      // 2. Optional variables that are provided in the variables object
      const filteredVariableDefinitions = node.variableDefinitions.filter((varDef: VariableDefinitionNode) => {
        const varName = varDef.variable.name.value
        const isRequired = varDef.type.kind === 'NonNullType'
        const isProvided = providedVariables.has(varName)
        const isUsed = usedVariables.has(varName)

        // Keep if: required OR (provided AND used in the query)
        return isRequired || (isProvided && isUsed)
      })

      return {
        ...node,
        variableDefinitions: filteredVariableDefinitions,
      }
    },

    Field(node) {
      if (!node.arguments || node.arguments.length === 0) {
        return node
      }

      // Filter arguments to remove those that reference unused variables
      const filteredArguments = node.arguments.reduce<ArgumentNode[]>((acc, arg) => {
        const filteredValue = filterValue(arg.value)
        if (filteredValue !== null) {
          acc.push({
            ...arg,
            value: filteredValue,
          })
        }
        return acc
      }, [])

      return {
        ...node,
        arguments: filteredArguments,
      }
    },
  })

  return print(transformedAst)
}

// Helper function to find a variable definition in the AST
function findVariableDefinition(ast: DocumentNode, variableName: string): VariableDefinitionNode | undefined {
  let varDef: VariableDefinitionNode | undefined

  visit(ast, {
    VariableDefinition(node: VariableDefinitionNode) {
      if (node.variable.name.value === variableName) {
        varDef = node
      }
    },
  })

  return varDef
}
